<template>
  <div v-if="isOpen" class="modal fade show d-block" tabindex="-1" @click.self="close">
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">🔍 Revisar Cambios - {{ dataTypeName }}</h5>
          <button type="button" class="btn-close" @click="close" aria-label="Close"></button>
        </div>

        <div class="modal-body">
        <div v-if="changes.length === 0" class="empty-state">
          <p>✅ No hay cambios detectados</p>
          <p class="text-muted">Los datos son idénticos</p>
        </div>

        <div v-else>
          <div class="summary-bar">
            <div class="summary-item added">
              <span class="badge">{{ addedCount }}</span> Añadidos
            </div>
            <div class="summary-item modified">
              <span class="badge">{{ modifiedCount }}</span> Modificados
            </div>
            <div class="summary-item removed">
              <span class="badge">{{ removedCount }}</span> Eliminados
            </div>
          </div>

          <div class="changes-list">
            <div
              v-for="(change, index) in changes"
              :key="index"
              class="change-item"
              :class="[change.type, { applied: appliedChanges.has(index), rejected: rejectedChanges.has(index) }]"
            >
              <div class="change-header">
                <span class="change-type-badge" :class="change.type">
                  {{ changeTypeLabel(change.type) }}
                </span>
                <span class="change-description">{{ change.field || change.description }}</span>
              </div>

              <div class="change-content">
                <!-- Mostrar el cambio individual -->
                <div class="field-diff-single" v-if="change.type === 'added'">
                  <div class="diff-section new">
                    <div class="diff-label">➕ Nuevo valor:</div>
                    <pre class="diff-value">{{ formatValue(change.newValue) }}</pre>
                  </div>
                </div>
                
                <div class="field-diff-single" v-else-if="change.type === 'removed'">
                  <div class="diff-section old">
                    <div class="diff-label">➖ Se eliminará:</div>
                    <pre class="diff-value">{{ formatValue(change.oldValue) }}</pre>
                  </div>
                </div>
                
                <div class="field-diff" v-else>
                  <div class="diff-section old">
                    <div class="diff-label">Anterior:</div>
                    <pre class="diff-value">{{ formatValue(change.oldValue) }}</pre>
                  </div>
                  <div class="diff-arrow">→</div>
                  <div class="diff-section new">
                    <div class="diff-label">Nuevo:</div>
                    <pre class="diff-value">{{ formatValue(change.newValue) }}</pre>
                  </div>
                </div>
              </div>

              <div class="change-actions">
                <button
                  v-if="!appliedChanges.has(index) && !rejectedChanges.has(index)"
                  class="btn btn-sm btn-success"
                  @click="acceptChange(index)"
                >
                  ✓ Aceptar
                </button>
                <button
                  v-if="!appliedChanges.has(index) && !rejectedChanges.has(index)"
                  class="btn btn-sm btn-danger"
                  @click="rejectChange(index)"
                >
                  ✕ Rechazar
                </button>
                <span v-if="appliedChanges.has(index)" class="status-badge accepted">
                  ✓ Aceptado
                </span>
                <span v-if="rejectedChanges.has(index)" class="status-badge rejected">
                  ✕ Rechazado
                </span>
                <button
                  v-if="appliedChanges.has(index) || rejectedChanges.has(index)"
                  class="btn btn-sm btn-secondary"
                  @click="undoDecision(index)"
                >
                  ↶ Deshacer
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <div class="footer-actions">
          <button class="btn btn-secondary" @click="close">Cancelar</button>
          <div class="bulk-actions">
            <button
              v-if="changes.length > 0"
              class="btn btn-outline"
              @click="acceptAll"
            >
              ✓ Aceptar Todos
            </button>
            <button
              v-if="changes.length > 0"
              class="btn btn-outline"
              @click="rejectAll"
            >
              ✕ Rechazar Todos
            </button>
            <button
              class="btn btn-primary"
              @click="applyAndClose"
              :disabled="appliedChanges.size === 0"
            >
              💾 Aplicar Cambios ({{ appliedChanges.size }})
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  </div>
  <div v-if="isOpen" class="modal-backdrop fade show"></div>
</template>

<script setup>
import { ref, computed, toRaw } from 'vue'

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  },
  changes: {
    type: Array,
    default: () => []
  },
  dataTypeName: {
    type: String,
    default: 'Datos'
  }
})

const emit = defineEmits(['close', 'apply'])

// Estado de cambios aplicados/rechazados
const appliedChanges = ref(new Set())
const rejectedChanges = ref(new Set())

// Contadores
const addedCount = computed(() => 
  props.changes.filter(c => c.type === 'added').length
)
const modifiedCount = computed(() => 
  props.changes.filter(c => c.type === 'modified').length
)
const removedCount = computed(() => 
  props.changes.filter(c => c.type === 'removed').length
)

// Etiquetas de tipo de cambio
function changeTypeLabel(type) {
  const labels = {
    added: '➕ AÑADIDO',
    modified: '✏️ MODIFICADO',
    removed: '➖ ELIMINADO'
  }
  return labels[type] || type
}

// Formatear JSON
function formatJson(obj) {
  if (obj === null) return 'null'
  if (obj === undefined) return 'undefined'
  if (typeof obj === 'object') {
    return JSON.stringify(obj, null, 2)
  }
  return String(obj)
}

// Formatear valor individual (no mostrar JSON completo de objetos)
function formatValue(val, depth = 0) {
  // Desempaquetar Proxy reactivo de Vue
  const rawVal = toRaw(val)
  
  if (rawVal === null) return 'null'
  if (rawVal === undefined) return 'undefined'
  
  // Para arrays, mostrar contenido expandido
  if (Array.isArray(rawVal)) {
    if (rawVal.length === 0) return '[]'
    
    // Para arrays pequeños, mostrar todos los elementos
    if (rawVal.length <= 5) {
      const items = rawVal.map(v => {
        const rawV = toRaw(v)
        if (typeof rawV === 'string') return `"${rawV}"`
        if (typeof rawV === 'number' || typeof rawV === 'boolean') return String(rawV)
        if (rawV === null) return 'null'
        if (typeof rawV === 'object') {
          if (Array.isArray(rawV)) {
            return `Array[${rawV.length}]`
          }
          // Expandir objetos dentro del array
          const keys = Object.keys(rawV)
          if (keys.length === 0) return '{}'
          
          if (keys.length <= 8) {
            const props = keys.map(k => `${k}: ${formatPrimitive(rawV[k])}`).join(', ')
            return `{${props}}`
          }
          
          // Para objetos más grandes, mostrar primeras propiedades
          const preview = keys.slice(0, 4).map(k => `${k}: ${formatPrimitive(rawV[k])}`).join(', ')
          return `{${preview}, ... +${keys.length - 4} más}`
        }
        return String(rawV)
      })
      return `[\n  ${items.join(',\n  ')}\n]`
    }
    
    // Para arrays grandes, mostrar resumen
    const preview = rawVal.slice(0, 3).map(v => {
      const rawV = toRaw(v)
      if (typeof rawV === 'string') return `"${rawV}"`
      if (typeof rawV === 'number' || typeof rawV === 'boolean') return String(rawV)
      if (typeof rawV === 'object') {
        if (Array.isArray(rawV)) return `Array[${rawV.length}]`
        const keys = Object.keys(rawV)
        if (keys.length <= 3) {
          const props = keys.map(k => `${k}: ${formatPrimitive(rawV[k])}`).join(', ')
          return `{${props}}`
        }
        return `Object{${keys.length}}`
      }
      return String(rawV)
    })
    return `Array[${rawVal.length}]:\n[\n  ${preview.join(',\n  ')},\n  ... (+${rawVal.length - 3} más)\n]`
  }
  
  // Para objetos, mostrar propiedades expandidas
  if (typeof rawVal === 'object') {
    const keys = Object.keys(rawVal)
    if (keys.length === 0) return '{}'
    
    // Mostrar todas las propiedades (hasta un límite razonable)
    if (keys.length <= 10 && depth === 0) {
      const props = keys.map(k => {
        const v = toRaw(rawVal[k])
        let formattedVal
        
        if (typeof v === 'string') {
          formattedVal = v.length > 50 ? `"${v.substring(0, 50)}..."` : `"${v}"`
        } else if (typeof v === 'number' || typeof v === 'boolean' || v === null) {
          formattedVal = String(v)
        } else if (Array.isArray(v)) {
          if (v.length === 0) {
            formattedVal = '[]'
          } else if (v.length <= 3) {
            formattedVal = `[${v.map(item => formatPrimitive(item)).join(', ')}]`
          } else {
            formattedVal = `Array[${v.length}]`
          }
        } else if (typeof v === 'object') {
          const subKeys = Object.keys(v)
          if (subKeys.length === 0) {
            formattedVal = '{}'
          } else if (subKeys.length <= 3) {
            formattedVal = `{${subKeys.map(sk => `${sk}: ${formatPrimitive(v[sk])}`).join(', ')}}`
          } else {
            formattedVal = `Object{${subKeys.length}}`
          }
        } else {
          formattedVal = String(v)
        }
        
        return `  ${k}: ${formattedVal}`
      })
      return `{\n${props.join(',\n')}\n}`
    }
    
    // Para objetos más grandes, mostrar resumen
    const preview = keys.slice(0, 3).map(k => {
      return `${k}: ${formatPrimitive(rawVal[k])}`
    }).join(', ')
    
    if (keys.length > 3) {
      return `{ ${preview}, ... (+${keys.length - 3} más) }`
    }
    return `{ ${preview} }`
  }
  
  // Para strings, añadir comillas
  if (typeof rawVal === 'string') {
    if (rawVal.length > 150) {
      return `"${rawVal.substring(0, 150)}..."`
    }
    return `"${rawVal}"`
  }
  
  return String(rawVal)
}

// Helper para formatear valores primitivos
function formatPrimitive(val) {
  const rawVal = toRaw(val)
  if (rawVal === null) return 'null'
  if (rawVal === undefined) return 'undefined'
  if (typeof rawVal === 'string') {
    if (rawVal.length > 40) return `"${rawVal.substring(0, 40)}..."`
    return `"${rawVal}"`
  }
  if (typeof rawVal === 'number' || typeof rawVal === 'boolean') return String(rawVal)
  if (Array.isArray(rawVal)) return `Array[${rawVal.length}]`
  if (typeof rawVal === 'object') return `Object{${Object.keys(rawVal).length}}`
  return String(rawVal)
}

// Formatear valores simples (para usar dentro de arrays/objetos) - DEPRECATED
function formatSimpleValue(val) {
  return formatPrimitive(val)
}

// Acciones
function acceptChange(index) {
  appliedChanges.value.add(index)
  rejectedChanges.value.delete(index)
}

function rejectChange(index) {
  rejectedChanges.value.add(index)
  appliedChanges.value.delete(index)
}

function undoDecision(index) {
  appliedChanges.value.delete(index)
  rejectedChanges.value.delete(index)
}

function acceptAll() {
  props.changes.forEach((_, index) => {
    appliedChanges.value.add(index)
  })
  rejectedChanges.value.clear()
}

function rejectAll() {
  props.changes.forEach((_, index) => {
    rejectedChanges.value.add(index)
  })
  appliedChanges.value.clear()
}

function close() {
  // Limpiar estado
  appliedChanges.value.clear()
  rejectedChanges.value.clear()
  emit('close')
}

function applyAndClose() {
  // Obtener solo los cambios aceptados
  const acceptedChanges = Array.from(appliedChanges.value).map(index => props.changes[index])
  
  emit('apply', acceptedChanges)
  close()
}
</script>

<style scoped>
/* Solo mantener estilos específicos del contenido, no del modal */
.empty-state {
  text-align: center;
  padding: 40px 20px;
}

.empty-state p {
  margin: 10px 0;
  font-size: 18px;
}

.text-muted {
  color: #999;
}

.summary-bar {
  display: flex;
  gap: 20px;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
}

[data-bs-theme="dark"] .summary-bar {
  background: #2a2a2a;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
}

.summary-item.added { color: #28a745; }
.summary-item.modified { color: #ffc107; }
.summary-item.removed { color: #dc3545; }

.summary-item .badge {
  background: currentColor;
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: bold;
}

.changes-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.change-item {
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  padding: 15px;
  transition: all 0.3s;
}

[data-bs-theme="dark"] .change-item {
  border-color: #333;
}

.change-item.added {
  border-left: 4px solid #28a745;
}

.change-item.modified {
  border-left: 4px solid #ffc107;
}

.change-item.removed {
  border-left: 4px solid #dc3545;
}

.change-item.applied {
  background: rgba(40, 167, 69, 0.1);
  border-color: #28a745;
}

.change-item.rejected {
  background: rgba(220, 53, 69, 0.1);
  border-color: #dc3545;
  opacity: 0.6;
}

.change-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.change-type-badge {
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  color: white;
}

.change-type-badge.added { background: #28a745; }
.change-type-badge.modified { background: #ffc107; color: #333; }
.change-type-badge.removed { background: #dc3545; }

.change-description {
  font-weight: 500;
  font-size: 15px;
}

.change-content {
  margin: 15px 0;
}

.diff-view {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.diff-section {
  padding: 10px;
  border-radius: 6px;
  border: 1px solid #ddd;
}

[data-bs-theme="dark"] .diff-section {
  border-color: #444;
}

.diff-section.old {
  background: #fff5f5;
  border-color: #ffc9c9;
}

[data-bs-theme="dark"] .diff-section.old {
  background: rgba(220, 53, 69, 0.1);
  border-color: rgba(220, 53, 69, 0.3);
}

.diff-section.new {
  background: #f0fff4;
  border-color: #9ae6b4;
}

[data-bs-theme="dark"] .diff-section.new {
  background: rgba(40, 167, 69, 0.1);
  border-color: rgba(40, 167, 69, 0.3);
}

.diff-label {
  font-weight: 600;
  margin-bottom: 8px;
  font-size: 13px;
  color: #666;
}

[data-bs-theme="dark"] .diff-label {
  color: #aaa;
}

.diff-code {
  margin: 0;
  padding: 10px;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
}

[data-bs-theme="dark"] .diff-code {
  background: rgba(0, 0, 0, 0.3);
}

.diff-value-full {
  padding: 10px 12px;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 13px;
  white-space: pre-wrap;
  word-break: break-word;
  color: #2c3e50;
  line-height: 1.6;
  max-height: 300px;
  overflow-y: auto;
}

[data-bs-theme="dark"] .diff-value-full {
  background: rgba(0, 0, 0, 0.3);
  color: #e0e0e0;
}

.field-changes {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.field-change {
  padding: 10px;
  background: #f8f9fa;
  border-radius: 6px;
}

.field-change.nested-field {
  margin-left: 20px;
  border-left: 3px solid #667eea;
  padding-left: 15px;
}

[data-bs-theme="dark"] .field-change {
  background: #2a2a2a;
}

.field-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.field-name {
  display: flex;
  align-items: center;
  gap: 5px;
  color: #667eea;
}

.nested-icon {
  color: #999;
  font-family: monospace;
}

.type-change-badge,
.array-badge,
.change-type-mini {
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}

.type-change-badge {
  background: #ff6b6b;
  color: white;
}

.array-badge {
  background: #4ecdc4;
  color: white;
}

.change-type-mini.added {
  background: #28a745;
  color: white;
}

.change-type-mini.removed {
  background: #dc3545;
  color: white;
}

.field-diff {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 10px;
  margin-top: 10px;
  align-items: center;
}

.field-diff-single {
  margin-top: 10px;
}

.diff-value {
  padding: 8px 12px;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 13px;
  word-break: break-word;
  color: #2c3e50;
  line-height: 1.6;
  white-space: pre-wrap;
  max-height: 400px;
  overflow-y: auto;
  margin: 0;
  overflow-x: auto;
}

[data-bs-theme="dark"] .diff-value {
  background: rgba(0, 0, 0, 0.3);
  color: #e0e0e0;
}

.diff-arrow {
  font-size: 20px;
  color: #667eea;
  font-weight: bold;
}

.change-actions {
  display: flex;
  gap: 10px;
  margin-top: 15px;
  align-items: center;
}

.status-badge {
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
}

.status-badge.accepted {
  background: #28a745;
  color: white;
}

.status-badge.rejected {
  background: #dc3545;
  color: white;
}

.modal-footer {
  padding: 20px 30px;
  border-top: 2px solid #e0e0e0;
}

[data-bs-theme="dark"] .modal-footer {
  border-top-color: #333;
}

.footer-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
  flex-wrap: wrap;
}

.bulk-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 13px;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #5568d3;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #5a6268;
}

.btn-success {
  background: #28a745;
  color: white;
}

.btn-success:hover {
  background: #218838;
}

.btn-danger {
  background: #dc3545;
  color: white;
}

.btn-danger:hover {
  background: #c82333;
}

.btn-outline {
  background: transparent;
  border: 2px solid #667eea;
  color: #667eea;
}

[data-bs-theme="dark"] .btn-outline {
  border-color: #8899ff;
  color: #8899ff;
}

.btn-outline:hover {
  background: #667eea;
  color: white;
}

[data-bs-theme="dark"] .btn-outline:hover {
  background: #8899ff;
  color: #1a1a1a;
}
</style>
